import axios, { AxiosResponse } from 'axios';
import {
  Admin,
  ROLE_SUPERADMIN,
  ROLE_SUPERADMINREAD,
  ROLE_TENANTADMIN,
} from 'constants/RBACConstants';
import _ from 'lodash';

import { UserSessionDto } from '../types/api/userSessionDto';
import { setLocalStorage } from './localStorageUtils';

declare global {
  interface Window {
    axios: typeof axios;
  }
}

export type UserData = UserSessionDto & {
  sessionData?: UserSessionDto;
};

interface AuthData {
  userData: UserData;
  active: boolean;
  csrfToken: string | null;
}

const authData: AuthData = {
  userData: {} as UserData,
  active: false,
  csrfToken: null,
};

export const resetUserAuthData = (): void => {
  authData.userData = {} as UserData;
  authData.active = false;
  authData.csrfToken = null;
};

export const updateSelectedThemeInCachedSessionData = (theme: string): void => {
  if (authData?.userData?.sessionData?.tenantDefaults) {
    authData.userData.sessionData.tenantDefaults.default_theme = theme;
  }
};

export const updateGodAccessibleInCachedSessionData = (
  accessible: boolean,
): void => {
  if (authData?.userData?.sessionData?.currentOrgSessionDto) {
    authData.userData.sessionData.currentOrgSessionDto.godAccessible =
      accessible;
  }
};

export function checkAuthenticity(refresh = true): Promise<UserData> {
  if (!_.isEmpty(authData.userData) && !refresh) {
    return Promise.resolve(authData.userData);
  }

  return new Promise((resolve, reject) => {
    const catchError = (e: any) => {
      resetUserAuthData();
      reject(e);
    };

    axios
      .get('/user/details/session')
      .then((sessionData: AxiosResponse<UserSessionDto>) => {
        const isLocalhost = Boolean(
          window.location.hostname === 'localhost' ||
            // [::1] is the IPv6 localhost address.
            window.location.hostname === '[::1]' ||
            // 127.0.0.0/8 are considered localhost for IPv4.
            window.location.hostname.match(
              /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
            ),
        );

        if (
          !isLocalhost &&
          sessionData?.data?.subDomain &&
          sessionData.data.subDomain !== window.location.hostname.split('.')[0]
        ) {
          window.location.href = window.location.href.replace(
            /https:\/\/(.*).armorcode/g,
            `https://${sessionData.data.subDomain}.armorcode`,
          );
        }

        axios
          .get('/user/details/role/permission')
          .then(({ data: permissionSet }) => {
            authData.userData = getUserSessionData(sessionData.data);
            authData.csrfToken = sessionData.headers['x-csrf-token'] as string;
            localStorage.setItem('CSRF', authData.csrfToken);
            authData.userData.permissionSet = permissionSet;
            authData.active = true;
            authData.userData.sessionData = sessionData.data;
            const userEmail = authData.userData.email;
            //set local storage with user email for populating email field in Okta login
            setLocalStorage('email', userEmail);
            resolve(authData.userData);
          })
          .catch(catchError);
      })
      .catch(catchError);
  });
}

export function logout(): Promise<void> {
  return axios
    .get('/logout', {
      headers: { Accept: '*/*' },
    })
    .then(() => {
      resetUserAuthData();
    });
}

export function signIn(data: UserSessionDto): Promise<AxiosResponse> {
  return axios.post('/public/login', data);
}

export function getLoginProviders(
  data: UserSessionDto,
): Promise<AxiosResponse> {
  return axios.get(
    !_.isEmpty(data.email)
      ? `public/filterLoginProviders?email=${data.email}`
      : 'public/filterLoginProviders',
  );
}

export function getUserData(): UserData {
  return { ...authData.userData };
}

export function getCsrfToken(): string | undefined {
  return localStorage.getItem('CSRF') || authData.csrfToken || undefined;
}

export function hasRole(roles: string | string[] = []): boolean {
  if (!Array.isArray(roles)) {
    roles = [roles];
  }
  return roles.includes(authData.userData.role ?? '');
}

export function hasPermission(
  permission: string | string[],
  partial = false,
): boolean {
  if (_.isArray(permission)) {
    const method = partial ? _.some : _.every;
    return method(
      permission,
      _.partial(_.has, authData.userData.permissionSet),
    );
  } else {
    return _.has(authData.userData.permissionSet, permission);
  }
}

export function isSuperOrTenantAdmin(): boolean {
  return hasRole([ROLE_SUPERADMIN, ROLE_TENANTADMIN]);
}

export function isSuperOrTenantAdminOrTenantRole(): boolean {
  return isSuperOrTenantAdmin() || !!authData.userData.tenantScope;
}

export function isAnyAdmin(): boolean {
  return hasRole([ROLE_SUPERADMIN, ROLE_TENANTADMIN, Admin]);
}

export function isSuperAdmin(): boolean {
  return hasRole([ROLE_SUPERADMIN]);
}

export function hasSuperAdminReadAccess(): boolean {
  return hasRole([ROLE_SUPERADMIN, ROLE_SUPERADMINREAD]);
}

export function isArmorCodeInternalUser(): boolean {
  const email = authData.userData?.email || '';
  return email.endsWith('@armorcode.io');
}

function getUserSessionData(
  data: UserSessionDto = {} as UserSessionDto,
): UserData {
  const userData = _.pick(data, [
    'id',
    'providerUserId',
    'email',
    'enabled',
    'displayName',
    'password',
    'role',
    'accessOnAllProduct',
    'roleId',
    'pendoAccountId',
  ]) as UserData;
  userData.organizationName = data.currentOrgSessionDto?.name;
  userData.organizationType = data.currentOrgSessionDto?.type;
  userData.businessUnitsName = data.currentBusinessSessionDto?.name;
  userData.businessUnitsDescription =
    data.currentBusinessSessionDto?.description;
  userData.businessUnitId = data.currentBusinessSessionDto?.id;
  userData.businessUnitRole = data.role;
  userData.tenantId = data.currentBusinessSessionDto?.tenant;
  userData.tenantCount = data.tenantCount;
  userData.tenantScope = data.tenantScope;
  userData.isArchiveBU = data.currentBusinessSessionDto?.isArchive;
  userData.assoiatedTeamRoleMap = data.assoiatedTeamRoleMap;

  return userData;
}
